class MakeInquiryDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.controller = new AbortController();
    this._preventScroll = (e) => {
      if (!this.panel.contains(e.target)) e.preventDefault();
    };
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-make-inquiry-drawer-panel]');
    this.overlay = this.querySelector('[data-make-inquiry-drawer-overlay]');
    this.closeButton = this.querySelector('[data-make-inquiry-drawer-close]');
    this.form = this.querySelector('[data-make-inquiry-form]');
    this.submitButton = this.querySelector('[data-inquiry-submit]');
    this.errorEl = this.querySelector('[data-inquiry-error]');
    this.successEl = this.querySelector('[data-inquiry-success]');
    this.product_title = this.dataset.productTitle;
    this.product_handle = this.dataset.productHandle;
    this.errorName = this.dataset.errorName;
    this.errorEmail = this.dataset.errorEmail;
    this.errorEmailInvalid = this.dataset.errorEmailInvalid;
    this.success = this.dataset.success;
    this.error = this.dataset.error;

    document
      .querySelectorAll('[data-make-an-inquiry-button]')
      .forEach((trigger) => {
        trigger.addEventListener('click', () => this.open(), {
          signal: this.controller.signal,
        });
      });

    this.closeButton?.addEventListener('click', () => this.close(), {
      signal: this.controller.signal,
    });
    this.overlay?.addEventListener('click', () => this.close(), {
      signal: this.controller.signal,
    });

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      },
      { signal: this.controller.signal }
    );

    this.form?.addEventListener('submit', (e) => this.handleSubmit(e), {
      signal: this.controller.signal,
    });

    this.querySelectorAll('[data-inquiry-size-option]').forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const pressed = btn.getAttribute('aria-pressed') === 'true';
          btn.setAttribute('aria-pressed', String(!pressed));
          const dot = btn.querySelector('[data-inquiry-size-text]');
          if (dot) {
            dot.classList.toggle('after:opacity-0', pressed);
            dot.classList.toggle('after:opacity-100', !pressed);
          }
        },
        { signal: this.controller.signal }
      );
    });

    this.querySelectorAll(
      '[data-size-chart-accordion], [data-inquiry-size-accordion]'
    ).forEach((details) => {
      details.addEventListener('toggle', () => {
        const icon = details.querySelector(
          '[data-size-chart-accordion-icon], [data-inquiry-size-accordion-icon]'
        );
        if (icon) icon.style.transform = details.open ? 'rotate(90deg)' : '';
      });
    });
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  validate(data) {
    if (!data.full_name.trim()) return this.errorName;
    if (!data.email.trim()) return this.errorEmail;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      return this.errorEmailInvalid;
    return null;
  }

  async handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(this.form);
    const selectedSizes = [
      ...this.querySelectorAll(
        '[data-inquiry-size-option][aria-pressed="true"]'
      ),
    ].map((btn) => btn.dataset.value);
    const data = {
      full_name: formData.get('full_name') || '',
      email: formData.get('email') || '',
      phone: formData.get('phone') || '',
      address: formData.get('address') || '',
      country: formData.get('country') || '',
      date_of_event: formData.get('date_of_event') || '',
      size: selectedSizes.join(', '),
      message: formData.get('message') || '',
      newsletter: formData.get('newsletter') === 'on',
      product_title: this.product_title,
      product_handle: this.product_handle,
    };

    const error = this.validate(data);
    if (error) {
      this.showError(error);
      return;
    }

    this.submitButton.disabled = true;
    this.hideMessages();

    try {
      const response = await fetch('https://formspree.io/f/mzblnrab', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        this.showSuccess(this.success);
        this.form.reset();
        this.querySelectorAll('[data-inquiry-size-option]').forEach((btn) => {
          btn.setAttribute('aria-pressed', 'false');
          const dot = btn.querySelector('[data-inquiry-size-text]');
          if (dot) {
            dot.classList.remove('after:opacity-100');
            dot.classList.add('after:opacity-0');
          }
        });
        this.triggerKlaviyoMetric(data);
      } else {
        this.showError(this.error);
      }
    } catch {
      this.showError(this.error);
    } finally {
      this.submitButton.disabled = false;
    }
  }

  triggerKlaviyoMetric(data) {
    if (typeof klaviyo === 'undefined') return;

    klaviyo.identify({ $email: data.email });

    setTimeout(() => {
      klaviyo.push([
        'track',
        'Made to Order',
        {
          'Product Title': this.product_title,
          'Product Handle': this.product_handle,
          'Full Name': data.full_name,
          Size: data.size,
          Venue: data.address,
          'Date of Event': data.date_of_event,
          Message: data.message,
          Newsletter: data.newsletter,
        },
      ]);
    }, 1000);
  }

  showError(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('hidden');
    }
    this.successEl?.classList.add('hidden');
  }

  showSuccess(message) {
    if (this.successEl) {
      this.successEl.textContent = message;
      this.successEl.classList.remove('hidden');
    }
    this.errorEl?.classList.add('hidden');
  }

  hideMessages() {
    this.errorEl?.classList.add('hidden');
    this.successEl?.classList.add('hidden');
  }

  open() {
    this.isOpen = true;
    this.panel.classList.remove('translate-x-full');
    this.panel.classList.add('translate-x-0');
    this.panel.setAttribute('aria-hidden', 'false');

    if (this.overlay) {
      this.overlay.classList.remove('opacity-0', 'pointer-events-none');
      this.overlay.classList.add('opacity-100');
    }

    document.body.style.overflow = 'hidden';
    document.addEventListener('touchmove', this._preventScroll, {
      passive: false,
    });
  }

  close() {
    this.isOpen = false;
    this.panel.classList.remove('translate-x-0');
    this.panel.classList.add('translate-x-full');
    this.panel.setAttribute('aria-hidden', 'true');

    if (this.overlay) {
      this.overlay.classList.remove('opacity-100');
      this.overlay.classList.add('opacity-0', 'pointer-events-none');
    }

    document.body.style.overflow = '';
    document.removeEventListener('touchmove', this._preventScroll);
  }
}

if (!customElements.get('make-inquiry-drawer')) {
  customElements.define('make-inquiry-drawer', MakeInquiryDrawer);
}
